import type { ReactNode } from 'react';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpFromBracket,
  faComment,
  faHeart,
} from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faHeart as faHeartSolid } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { styled, theme } from '@components-shared/src/stitches';
import { Button } from '@components/main-components/button/Button';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import type { CurrentListeningParty } from '@hooks/usePlayButton';
import type { DropdownAlign } from '../Dropdowns/GlobalDropdown';
import { MoreDropdown } from '../Dropdowns/MoreDropdown';

export const ActionBar = ({
  isLiked = false,
  isLikeButtonDisabled = false,
  onLikeButtonClick,
  onShareButtonClick,
  onCommentButtonClick,
  isPlaying,
  isPlayButtonLoading = false,
  isPlayButtonDisabled = false,
  onPlayButtonClick,
  moreDropdownItems,
  playButtonRef,
  isPreview = false,
  align = 'end',
  className,
  isShareReferrable,
  currentListeningParty,
}: {
  isLiked?: boolean;
  isLikeButtonDisabled?: boolean;
  onLikeButtonClick?: () => void;
  onShareButtonClick?: () => void;
  onCommentButtonClick?: () => void;
  isPlaying?: boolean;
  isPlayButtonLoading?: boolean;
  isPlayButtonDisabled?: boolean;
  onPlayButtonClick?: () => void;
  moreDropdownItems?: ReactNode;
  playButtonRef?: (node?: Element | null) => void;
  isPreview?: boolean;
  align?: DropdownAlign;
  className?: string;
  isShareReferrable: boolean;
  currentListeningParty: CurrentListeningParty;
}) => {
  return (
    <Container className={className}>
      <Buttons>
        {(onLikeButtonClick || isPreview) && (
          <>
            <ButtonDesktop
              size="S"
              variant="tertiary"
              label={isLiked ? 'Liked' : 'Like'}
              leadingIcon={
                <FontAwesomeIcon
                  icon={isLiked ? faHeartSolid : faHeart}
                  color={
                    theme.colors[
                      isLiked
                        ? 'fuchsia500'
                        : isLikeButtonDisabled || isPreview
                        ? 'neutral300'
                        : 'base800'
                    ].value
                  }
                />
              }
              onClick={onLikeButtonClick}
              disabled={isLikeButtonDisabled || isPreview}
            />
            <ButtonMobile
              icon={
                <FontAwesomeIcon
                  icon={isLiked ? faHeartSolid : faHeart}
                  color={theme.colors[isLiked ? 'fuchsia500' : 'base500'].value}
                  size="lg"
                />
              }
              onClick={onLikeButtonClick}
              disabled={isLikeButtonDisabled || isPreview}
            />
          </>
        )}
        {onCommentButtonClick && (
          <>
            <ButtonDesktop
              size="S"
              variant="tertiary"
              label="Comment"
              leadingIcon={<FontAwesomeIcon icon={faComment} />}
              onClick={onCommentButtonClick}
            />
            <ButtonMobile
              icon={<FontAwesomeIcon icon={faComment} size="lg" />}
              onClick={onCommentButtonClick}
            />
          </>
        )}
        <ButtonDesktop
          size="S"
          variant="tertiary"
          label={isShareReferrable ? 'Share & Earn' : 'Share'}
          leadingIcon={<FontAwesomeIcon icon={faArrowUpFromBracket} />}
          onClick={onShareButtonClick}
          disabled={isPreview}
        />
        <ButtonMobile
          icon={<FontAwesomeIcon icon={faArrowUpFromBracket} size="lg" />}
          onClick={onShareButtonClick}
        />
        <MoreDropdown align={align} disabled={isPreview}>
          {moreDropdownItems}
        </MoreDropdown>
      </Buttons>
      <PlayButtonWrapper>
        <PlayButton
          playing={isPlaying}
          onClick={onPlayButtonClick}
          color="black"
          size="m"
          ref={playButtonRef}
          loading={isPlayButtonLoading}
          disabled={isPlayButtonDisabled}
          currentListeningParty={currentListeningParty}
        />
      </PlayButtonWrapper>
    </Container>
  );
};

const Container = styled('div', {
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  height: 84,
  paddingTop: 12,
  '@tablet': {
    paddingTop: 0,
  },
});

const ButtonDesktop = styled(Button, {
  display: 'none !important',
  padding: '9px 12px',
  lineHeight: 'unset',
  color: '$base800',
  '@tablet': {
    display: 'flex !important',
  },
});

const ButtonMobile = styled(IconButton, {
  'button&': {
    '@tablet': {
      display: 'none',
    },
  },
});

const Buttons = styled('div', {
  display: 'flex',
  gap: 10,
  alignItems: 'center',
});

const PlayButtonWrapper = styled('div', {
  display: 'flex',
  '@tablet': {
    display: 'none',
  },
});
